import React, { useState, useEffect } from 'react';
import { getAllGrades, createGrade, updateGrade, deleteGrade, getGradeById } from '../services/api';

const Grades = () => {
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingGrade, setEditingGrade] = useState(null);
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [formData, setFormData] = useState({
    nom: '',
    code: '',
    description: '',
    niveau_hierarchique: 0,
    statut: 'actif',
  });

  useEffect(() => {
    loadGrades();
  }, []);

  const loadGrades = async () => {
    setLoading(true);
    try {
      const data = await getAllGrades();
      setGrades(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erreur lors du chargement des grades:', error);
      setMessage({ type: 'error', text: 'Erreur lors du chargement des grades' });
      setGrades([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: name === 'niveau_hierarchique' ? parseInt(value) || 0 : value }));
  };

  const handleCreateGrade = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (!formData.nom) {
      setMessage({ type: 'error', text: 'Le nom est requis' });
      return;
    }

    setLoading(true);
    try {
      await createGrade(formData);
      setMessage({ type: 'success', text: 'Grade créé avec succès' });
      setShowCreateForm(false);
      resetForm();
      loadGrades();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Erreur lors de la création du grade',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateGrade = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    setLoading(true);
    try {
      await updateGrade(editingGrade.id, formData);
      setMessage({ type: 'success', text: 'Grade mis à jour avec succès' });
      setEditingGrade(null);
      resetForm();
      loadGrades();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Erreur lors de la mise à jour du grade',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGrade = async (gradeId, nom) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le grade "${nom}" ?`)) {
      return;
    }

    setLoading(true);
    try {
      await deleteGrade(gradeId);
      setMessage({ type: 'success', text: 'Grade supprimé avec succès' });
      loadGrades();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Erreur lors de la suppression du grade',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditGrade = (grade) => {
    setEditingGrade(grade);
    setFormData({
      nom: grade.nom || '',
      code: grade.code || '',
      description: grade.description || '',
      niveau_hierarchique: grade.niveau_hierarchique || 0,
      statut: grade.statut || 'actif',
    });
    setShowCreateForm(true);
  };

  const handleViewDetails = async (id) => {
    setLoadingDetails(true);
    setShowDetails(true);
    try {
      const grade = await getGradeById(id);
      setSelectedGrade(grade);
    } catch (error) {
      console.error('Erreur lors du chargement des détails:', error);
      setMessage({ type: 'error', text: 'Erreur lors du chargement des détails' });
    } finally {
      setLoadingDetails(false);
    }
  };

  const resetForm = () => {
    setFormData({
      nom: '',
      code: '',
      description: '',
      niveau_hierarchique: 0,
      statut: 'actif',
    });
    setEditingGrade(null);
  };

  const handleCancel = () => {
    setShowCreateForm(false);
    resetForm();
    setMessage({ type: '', text: '' });
  };

  // Pagination
  const totalPages = Math.ceil(grades.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentGrades = grades.slice(startIndex, endIndex);

  return (
    <div className="p-8 bg-background min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-text flex items-center">
            <i className="fas fa-graduation-cap mr-3 text-primary"></i>
            Gestion des Grades
          </h1>
          <button
            onClick={() => {
              resetForm();
              setShowCreateForm(true);
            }}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-all"
          >
            <i className="fas fa-plus mr-2"></i> Nouveau Grade
          </button>
        </div>

        {message.text && (
          <div
            className={`mb-4 p-4 rounded-lg ${
              message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}
          >
            {message.text}
          </div>
        )}

        {showCreateForm && (
          <div className="bg-cardBg rounded-lg shadow-lg p-6 mb-6">
            <h2 className="text-xl font-semibold text-text mb-4">
              {editingGrade ? 'Modifier le Grade' : 'Nouveau Grade'}
            </h2>
            <form onSubmit={editingGrade ? handleUpdateGrade : handleCreateGrade}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text mb-2">Nom *</label>
                  <input
                    type="text"
                    name="nom"
                    value={formData.nom}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text mb-2">Code</label>
                  <input
                    type="text"
                    name="code"
                    value={formData.code}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-text mb-2">Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text mb-2">Niveau Hiérarchique</label>
                  <input
                    type="number"
                    name="niveau_hierarchique"
                    value={formData.niveau_hierarchique}
                    onChange={handleInputChange}
                    min="0"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text mb-2">Statut</label>
                  <select
                    name="statut"
                    value={formData.statut}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="actif">Actif</option>
                    <option value="inactif">Inactif</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-4 mt-6">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-4 py-2 border border-gray-300 text-text rounded-lg hover:bg-gray-100 transition-all"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-all disabled:opacity-50"
                >
                  {loading ? 'Enregistrement...' : editingGrade ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        )}

        {loading && !showCreateForm ? (
          <div className="text-center py-8">
            <i className="fas fa-spinner fa-spin text-4xl text-primary mb-4"></i>
            <p className="text-text-light">Chargement des grades...</p>
          </div>
        ) : (
          <>
            <div className="bg-cardBg rounded-lg shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Niveau</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentGrades.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="px-6 py-4 text-center text-text-light">
                          Aucun grade trouvé
                        </td>
                      </tr>
                    ) : (
                      currentGrades.map((grade) => (
                        <tr key={grade.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-text">{grade.id}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-text">{grade.nom}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-text">{grade.code || '-'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-text">{grade.niveau_hierarchique}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`px-2 py-1 text-xs rounded-full ${
                                grade.statut === 'actif'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {grade.statut}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                            <button
                              onClick={() => handleViewDetails(grade.id)}
                              className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all text-sm"
                              title="Voir les détails"
                            >
                              <i className="fas fa-eye"></i>
                            </button>
                            <button
                              onClick={() => handleEditGrade(grade)}
                              className="px-3 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-all text-sm"
                              title="Modifier"
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            <button
                              onClick={() => handleDeleteGrade(grade.id, grade.nom)}
                              className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all text-sm"
                              title="Supprimer"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-4 flex justify-center items-center gap-2">
                <button
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                >
                  <i className="fas fa-chevron-left"></i>
                </button>
                <span className="text-text">
                  Page {currentPage} sur {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                >
                  <i className="fas fa-chevron-right"></i>
                </button>
              </div>
            )}
          </>
        )}

        {/* Modal Détails */}
        {showDetails && selectedGrade && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-text">Détails du Grade</h2>
                <button
                  onClick={() => {
                    setShowDetails(false);
                    setSelectedGrade(null);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <i className="fas fa-times text-xl"></i>
                </button>
              </div>
              {loadingDetails ? (
                <div className="text-center py-8">
                  <i className="fas fa-spinner fa-spin text-4xl text-primary mb-4"></i>
                  <p className="text-text-light">Chargement...</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-text-light">ID</label>
                    <p className="text-text">{selectedGrade.id}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-text-light">Nom</label>
                    <p className="text-text">{selectedGrade.nom}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-text-light">Code</label>
                    <p className="text-text">{selectedGrade.code || '-'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-text-light">Description</label>
                    <p className="text-text">{selectedGrade.description || '-'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-text-light">Niveau Hiérarchique</label>
                    <p className="text-text">{selectedGrade.niveau_hierarchique}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-text-light">Statut</label>
                    <p className="text-text">
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          selectedGrade.statut === 'actif'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {selectedGrade.statut}
                      </span>
                    </p>
                  </div>
                  {selectedGrade.date_creation && (
                    <div>
                      <label className="text-sm font-medium text-text-light">Date de Création</label>
                      <p className="text-text">{new Date(selectedGrade.date_creation).toLocaleString('fr-FR')}</p>
                    </div>
                  )}
                  {selectedGrade.date_modification && (
                    <div>
                      <label className="text-sm font-medium text-text-light">Date de Modification</label>
                      <p className="text-text">{new Date(selectedGrade.date_modification).toLocaleString('fr-FR')}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Grades;

